import WebUploader from "webuploader";
import "webuploader/dist/webuploader.css";

import * as zip from "@zip.js/zip.js";
import logServeCof from '@/plugins/logServeCof.js';
import Evented from '@/utils/Evented.js';
import { Store } from "@/utils/store";

import Message from '@/utils/resetMessage';
import { MessageBox } from 'element-ui';
import { getToken } from "@/utils/generalMethods";

import {
    createImport,
    dataSetImportCheckService,
    sceneImportCheckService,
    projImportCheckService,
    pauseImportService,
    getFileInfoService
} from "@/service/interface";

const BLOB_SIZE = 20 * 1024 * 1024;

class ImportUploader extends Evented {
    constructor(schemeType) {
        super();
        this.schemeType = schemeType;
        this.importType = 0;    // 导入类型(0: 项目, 1: 场景, 2: 数据集)

        // status为前端自定义状态，状态说明如下：
        // waiting：某个文件等待上传
        // created: 某个文件已创建上传索引
        // continue：某个文件断点续传(不需要重新选择文件)
        // reContinue: 某个文件断点续传(需要重新选择文件)
        // paused: 某个文件已暂停上传,
        this.status = "waiting";

        this.queueFiles = [];       // 每次上传选择的文件
        this.uploadFiles = [];      // 进入上传队列的文件
        this.uploadingFiles = [];   // 上传中的文件
        this.pausedFiles = [];      // 上传暂停的文件
        this.continueFlag = false;
        this.currFileInfo = null;   // 设置文件信息，用于断点续传
        this.fileTotal = 0;         // 本次导入文件总数
        this.invalidFiles = [];     // 异常文件

        this.onFilesQueued = this.onFilesQueued.bind(this);
        this.onUploadBeforeSend = this.onUploadBeforeSend.bind(this);
        this.onUploadAccept = this.onUploadAccept.bind(this);
        this.onUploadSuccess = this.onUploadSuccess.bind(this);
        this.onUploadError = this.onUploadError.bind(this);

        this.importFiles = this.importFiles.bind(this);
        this.cancelImport = this.cancelImport.bind(this);
        this.onPauseFileUpload = this.onPauseFileUpload.bind(this);
        this.onContinueFileUpload = this.onContinueFileUpload.bind(this);

        this.initUploader();
        this.addUploaderEvents(this.uploader);

        this.addEvnetListener();
    }

    initUploader() {
        const { uploaders } = Store;
        const find = uploaders.find(el => el.schemeType === this.schemeType);
        if (!find) {
            this.uploader = this.createUploader();
            uploaders.push({ schemeType: this.schemeType, uploader: this });
        }
    }

    createUploader() {
        let webUploader = WebUploader.create({
            // swf文件路径
            swf: "../../node_modules/webuploader/dist/Uploader.swf",

            timeout: window.uploadTimeout,

            accept: {
                title: "数据导入",
                extensions: "zip,rar,7z",
                mimeTypes: "application/zip",
            },
            chunked: true,
            chunkSize: BLOB_SIZE,
            fileVal: "FileInfo",
            threads: 1,
            chunkRetry: 0,
            duplicate: true,

            auto: false,
        });
        return webUploader;
    }

    addUploaderEvents(uploader) {
        uploader.on("filesQueued", this.onFilesQueued);
        uploader.on("uploadBeforeSend", this.onUploadBeforeSend);
        uploader.on("uploadAccept", this.onUploadAccept);
        uploader.on("uploadSuccess", this.onUploadSuccess);
        uploader.on("uploadError", this.onUploadError);
    }

    addEvnetListener() {
        this.on('importFiles', this.importFiles);
        this.on("cancelImport", this.cancelImport);
        this.on('pauseFileUpload', this.onPauseFileUpload);
        this.on('continueFileUpload', this.onContinueFileUpload);
    }

    onFilesQueued(files) {
        this.fileTotal = files.length;

        let promiseArray = [];
        let filesInUploadQueue = [];
        files.forEach(file => {
            file.fileName = file.name;
            file.length = file.size;
            file.schemeType = this.schemeType;
            file.importType = this.importType;

            let fileInfo = {
                id: WebUploader.Base.guid(),
                importType: this.importType,
                schemeType: this.schemeType,
                customStatus: this.status,

                // 是否已经经过导入校验(断点续传，不需要重新选择文件时视为已校验过)
                isChecked: this.status === 'continue' ? true : false,

                // 以下为上传列表中需要展示的内容
                fileName: file.name,
                length: file.size,
                creationTime: new Date().toUTCString(),
                rate: 0,
                status: this.getFileStatus(),

                // 文件对象
                file,
            }

            if (this.status === 'continue' || this.status === 'reContinue') {
                if (this.currFileInfo.fileName !== fileInfo.fileName) {
                    Message.warning("文件名不匹配，请重试");
                    return;
                }
                fileInfo["fileDataId"] = this.currFileInfo.fileDataId;
            }

            let findFile = this.uploadFiles.find(el => this.isSameFile(el, fileInfo));
            let findUploadingFile = this.uploadingFiles.find(el => this.isSameFile(el, fileInfo));
            if (findFile || findUploadingFile) {
                fileInfo.isChecked = true;
                filesInUploadQueue.push(fileInfo);
            } else {
                let promise = this.analysisZipFile(fileInfo);
                promiseArray.push(promise);
            }
        });

        if (filesInUploadQueue.length) {
            let files = filesInUploadQueue.map(el => {
                return { id: el.id, fileName: el.fileName, length: el.length, file: el.file }
            })
            let obj = {
                isMsgBox: false,
                isOverWrite: false,
                errMsg: '已在上传队列',
                files
            }
            this.invalidFiles.push(obj);
        }

        Promise.all(promiseArray).then((response) => {
            let invalidImportTypeFiles = [];
            response.forEach(item => {
                if (this.isInvalidImportTypeFile(item)) {
                    item.isChecked = true;
                    let obj = {
                        id: item.id,
                        fileName: item.fileName,
                        file: item.file,
                        length: item.length,
                    }
                    invalidImportTypeFiles.push(obj)
                } else {
                    this.queueFiles.push(item);
                }
            });

            if (invalidImportTypeFiles.length) {
                let obj = {
                    isMsgBox: true,
                    isOverWrite: false,
                    errMsg: '文件数据类型错误',
                    files: invalidImportTypeFiles
                }
                this.invalidFiles.push(obj);
            }

            if (this.status === 'continue' || this.status === 'reContinue') {
                this.continueUploadFiles();
            } else {
                let importFilesInfo = [];

                importFilesInfo = response.map(item => {
                    let obj = {
                        schemeType: item.zipScheme,
                        formatType: item.zipFormatType,
                    }
                    return obj;
                })

                this.fire('importFilesQueued', { importType: this.importType, importFilesInfo });
            }
        })
    }

    importFiles(eventInfo) {
        if (!this.queueFiles.length) {
            this.handleInvalidFile().then(() => {
                this.handleWarningFile().then(() => {
                    this.cancelImport();
                });
            });
            return;
        }

        this.uploadFiles = this.queueFiles.map(item => item);
        this.queueFiles = [];

        let params = eventInfo.importParams;
        if (this.importType === 0) {
            this.importProject(params);
        }
        if (this.importType === 1) {
            this.importScene(params);
        }
        if (this.importType === 2) {
            this.importDataset(params);
        }
    }

    continueUploadFiles() {
        this.uploadFiles = this.queueFiles.map(item => item);
        this.queueFiles = [];
        if (this.importType === 0) {
            this.continueUploadProj();
        }
        if (this.importType === 1) {
            this.continueUploadScene();
        }
        if (this.importType === 2) {
            this.continueUploadDataSet();
        }
    }

    cancelImport() {
        this.importType = 0;
        this.status = "waiting";
        this.queueFiles = [];
        this.uploadFiles = [];
        this.uploadingFiles = [];
        this.pausedFiles = [];
        this.currFileInfo = null;
        this.fileTotal = 0;
        this.invalidFiles = [];
    }

    importProject(params) {
        let unCheckFiles = this.uploadFiles.filter(fileInfo => !fileInfo.isChecked);
        unCheckFiles.forEach(item => {
            item.ownerId = params.ownerId;
        });
        this.projInfoCheck(unCheckFiles, params.ownerId).then(() => {
            this.handleInvalidFile().then(() => {
                this.handleWarningFile().then(() => {
                    if (this.uploadFiles.length) {
                        if (this.uploadingFiles.length) {
                            Message.warning("当前有文件正在上传，请耐心等待");
                            return;
                        }
                        let uploadingFile = this.uploadFiles.shift();
                        this.uploadingFiles.push(uploadingFile);
                        this.fire('startUploadFile', {});
                        this.createFileUpLoad(uploadingFile).then(() => {
                            uploadingFile.customStatus = "created";
                            uploadingFile.status = 0;

                            this.uploader.upload();
                            this.fire('update-importlist', {});
                        });
                    }
                })
            })
        })
    }

    importScene(params) {
        let unCheckFiles = this.uploadFiles.filter(fileInfo => !fileInfo.isChecked);
        unCheckFiles.forEach(item => {
            item.projId = params.projId;
        });
        this.sceneCheck(unCheckFiles, params.projId).then(() => {
            this.handleInvalidFile().then(() => {
                this.handleWarningFile().then(() => {
                    if (this.uploadFiles.length) {
                        if (this.uploadingFiles.length) {
                            Message.warning("当前有文件正在上传，请耐心等待");
                            return;
                        }
                        let uploadingFile = this.uploadFiles.shift();
                        this.uploadingFiles.push(uploadingFile);
                        this.fire('startUploadFile', {});
                        this.createFileUpLoad(uploadingFile).then(() => {
                            uploadingFile.customStatus = "created";
                            uploadingFile.status = 0;

                            this.uploader.upload();
                            this.fire('update-importlist', {});
                        });
                    }
                })
            })
        })
    }

    importDataset(params) {
        let unCheckFiles = this.uploadFiles.filter(fileInfo => !fileInfo.isChecked);
        unCheckFiles.forEach(item => {
            item.projId = params.projId;
            item.nodeId = item.zipId;
            item.dataScheme = item.zipScheme;
            item.formatType = params.zipFormatType;
            item.targetNodeId = params.targetNodeId;
        });
        this.dataSetCheck(unCheckFiles, params.projId).then(() => {
            this.handleInvalidFile().then(() => {
                this.handleWarningFile().then(() => {
                    if (this.uploadFiles.length) {
                        if (this.uploadingFiles.length) {
                            Message.warning("当前有文件正在上传，请耐心等待");
                            return;
                        }
                        let uploadingFile = this.uploadFiles.shift();
                        this.uploadingFiles.push(uploadingFile);
                        this.fire('startUploadFile', {});
                        this.createFileUpLoad(uploadingFile).then(() => {
                            uploadingFile.customStatus = "created";
                            uploadingFile.status = 0;

                            this.uploader.upload();
                            this.fire('update-importlist', {});
                        });
                    }
                })
            })
        })
    }

    continueUploadProj() {
        let ownerId = this.currFileInfo.ownerId;
        let unCheckFiles = this.uploadFiles.filter(fileInfo => !fileInfo.isChecked);
        unCheckFiles.forEach(item => {
            item.ownerId = ownerId;
        });
        if (!unCheckFiles.length) {
            if (this.uploadingFiles.length) {
                Message.warning("当前有文件正在上传，请耐心等待");
                return;
            }
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.fire('startUploadFile', {});
            this.getFileInfo(uploadingFile).then(() => {
                uploadingFile.customStatus = "continue";
                uploadingFile.status = 1;

                this.continueFlag = true;
                this.setAppendUrl('reAppend');
                this.uploader.upload();
            });

            return;
        }
        this.projInfoCheck(unCheckFiles, ownerId).then(() => {
            this.handleInvalidFile().then(() => {
                this.handleWarningFile().then(() => {
                    if (this.uploadingFiles.length) {
                        Message.warning("当前有文件正在上传，请耐心等待");
                        return;
                    }
                    let uploadingFile = this.uploadFiles.shift();
                    this.uploadingFiles.push(uploadingFile);
                    this.fire('startUploadFile', {});
                    this.getFileInfo(uploadingFile).then(() => {
                        uploadingFile.customStatus = "continue";
                        uploadingFile.status = 1;

                        this.continueFlag = true;
                        this.setAppendUrl('reAppend');
                        this.uploader.upload();
                    });
                })
            })
        })
    }

    continueUploadScene() {
        let projId = this.currFileInfo.projId;
        let unCheckFiles = this.uploadFiles.filter(fileInfo => !fileInfo.isChecked);
        unCheckFiles.forEach(item => {
            item.projId = projId;
        });
        if (!unCheckFiles.length) {
            if (this.uploadingFiles.length) {
                Message.warning("当前有文件正在上传，请耐心等待");
                return;
            }
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.fire('startUploadFile', {});
            this.getFileInfo(uploadingFile).then(() => {
                uploadingFile.customStatus = "continue";
                uploadingFile.status = 1;

                this.continueFlag = true;
                this.setAppendUrl('reAppend');
                this.uploader.upload();
            });
            return;
        }
        this.sceneCheck(unCheckFiles, projId).then(() => {
            this.handleInvalidFile().then(() => {
                this.handleWarningFile().then(() => {
                    if (this.uploadingFiles.length) {
                        Message.warning("当前有文件正在上传，请耐心等待");
                        return;
                    }
                    let uploadingFile = this.uploadFiles.shift();
                    this.uploadingFiles.push(uploadingFile);
                    this.fire('startUploadFile', {});
                    this.getFileInfo(uploadingFile).then(() => {
                        uploadingFile.customStatus = "continue";
                        uploadingFile.status = 1;

                        this.continueFlag = true;
                        this.setAppendUrl('reAppend');
                        this.uploader.upload();
                    });
                })
            })
        })
    }

    continueUploadDataSet() {
        let projId = this.currFileInfo.projId;
        let unCheckFiles = this.uploadFiles.filter(fileInfo => !fileInfo.isChecked);
        unCheckFiles.forEach(item => {
            item.projId = projId;
            item.nodeId = item.zipId;
            item.dataScheme = item.zipScheme;
            item.formatType = item.zipFormatType;
        });
        if (!unCheckFiles.length) {
            if (this.uploadingFiles.length) {
                Message.warning("当前有文件正在上传，请耐心等待");
                return;
            }
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.fire('startUploadFile', {});
            this.getFileInfo(uploadingFile).then(() => {
                uploadingFile.customStatus = "continue";
                uploadingFile.status = 1;

                this.continueFlag = true;
                this.setAppendUrl('reAppend');
                this.uploader.upload();
            });
            return;
        }
        this.dataSetCheck(unCheckFiles, projId).then(() => {
            this.handleInvalidFile().then(() => {
                this.handleWarningFile().then(() => {
                    if (this.uploadingFiles.length) {
                        Message.warning("当前有文件正在上传，请耐心等待");
                        return;
                    }
                    let uploadingFile = this.uploadFiles.shift();
                    this.uploadingFiles.push(uploadingFile);
                    this.fire('startUploadFile', {});
                    this.getFileInfo(uploadingFile).then(() => {
                        uploadingFile.customStatus = "continue";
                        uploadingFile.status = 1;

                        this.continueFlag = true;
                        this.setAppendUrl('reAppend');
                        this.uploader.upload();
                    });
                })
            })
        })
    }

    onUploadBeforeSend(object, data, headers) {
        headers["Authorization"] = getToken();

        data["Index"] = data.chunk ? data.chunk : 0;

        if (this.continueFlag) {
            let importType = object.file.importType;
            if (importType === 0) {
                this.setAppendUrl("project");
            } else if (importType === 1) {
                this.setAppendUrl("scene");
            } else {
                this.setAppendUrl("dataset");
            }
            this.continueFlag = false;
        }
    }

    onUploadAccept() {
        let data = this.uploader.options.formData;
        if (data.pauseFlag) {
            // webuploader的stop方法有两种模式
            // 1、暂停自动上传下个文件，但是当前文件会继续传完。
            // 2、暂停自动上传下个文件，且中断当前文件上传。(如果是分块上传，只会中断当前分块)
            // 此处需要暂停当前文件的继续上传，因此需要传入true
            this.uploader.stop(true);
            this.pauseFileUpload(data.pauseInfo);
        }
    }

    onUploadSuccess(file, response) {
        if (!response.isSuccess) {
            Message.error(response.errMsg);
        } else {
            this.fire('refreshImportList', {})
        }

        let findIndex = this.uploadingFiles.findIndex((el) => el.fileName === file.name && el.length === file.size);
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            if (!this.uploadFiles.length) return;


            let findCheckedIndex = this.uploadFiles.findIndex(el => el.isChecked);
            if (findCheckedIndex !== -1) {
                let uploadingFile = this.uploadFiles[findCheckedIndex];
                this.uploadFiles.splice(findCheckedIndex, 1)
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    onUploadError(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => el.fileName === file.name && el.length === file.size);
        if (findIndex !== -1) {
            this.uploader.removeFile(file);
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.stop();
            if (!this.uploadFiles.length) return;
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.handleUploadSuccess(uploadingFile);
        }
    }

    onPauseFileUpload(eventInfo) {
        let { fileInfo } = eventInfo;

        let find = this.uploadingFiles.find(el => this.isSameFile(el, fileInfo));
        if (find) {
            this.uploader.options.formData.pauseFlag = true;
            this.uploader.options.formData.pauseInfo = {
                fileDataId: fileInfo.fileDataId,
                key: fileInfo.key,
                fileName: fileInfo.fileName,
                length: fileInfo.length,
            }
        } else {
            this.pauseFileUpload(fileInfo);
        }
    }

    pauseFileUpload(pauseInfo) {
        let params = { fileDataIds: [pauseInfo.fileDataId], keys: [pauseInfo.key] }
        pauseImportService(params).then((response) => {
            if (response.isSuccess) {
                this.handleFilePauseSuccess(pauseInfo);
            } else {
                Message.error(response.errMsg);
            }
        });
    }

    onContinueFileUpload(eventInfo) {
        let { fileInfo } = eventInfo;
        let findIndex = this.pausedFiles.findIndex((el) => el.fileDataId === fileInfo.fileDataId);
        if (findIndex !== -1) {
            let uploadingFile = this.pausedFiles[findIndex];
            this.pausedFiles.splice(findIndex, 1);

            this.continueFlag = true;
            this.setAppendUrl('reAppend');

            this.uploader.addFiles(uploadingFile.file);
        }
    }

    /**
     * 处理文件暂停成功
     * @param {Object} pauseInfo 
     */
    handleFilePauseSuccess(pauseInfo) {
        Message.success("文件暂停成功");
        delete this.uploader.options.formData.pauseFlag;
        delete this.uploader.options.formData.pauseInfo;
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, pauseInfo));
        if (findIndex !== -1) {
            let fileInfo = this.uploadingFiles[findIndex];
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(fileInfo.file, true);

            fileInfo.customStatus = 'paused';
            fileInfo.status = 2;
            fileInfo.fileDataId = pauseInfo.fileDataId;
            this.pausedFiles.push(fileInfo);
            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    projInfoCheck(files, targetOwnerId) {
        return new Promise(resolve => {
            let paramsFiles = files.map(fileInfo => {
                let obj = {
                    projId: fileInfo.zipId,
                    projName: fileInfo.zipName,
                }
                return obj;
            });
            let params = { targetOwnerId, files: paramsFiles };
            projImportCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    let unEffectiveItems = data.unEffectiveItems;
                    let invalidFiles = [
                        {
                            isMsgBox: true,
                            isOverWrite: true,
                            errMsg: '项目信息已存在',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '项目名称和已有项目重复',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '项目信息已导入给其他用户',
                            files: []
                        }
                    ]
                    unEffectiveItems.forEach(item => {
                        let findIndex = invalidFiles.findIndex(el => item.errorMsg.includes(el.errMsg));
                        if (findIndex !== -1) {
                            let find = files.find(el => el.zipName === item.fileName);
                            let obj = { id: find.id, fileName: find.fileName, length: find.length, file: find.file }
                            invalidFiles[findIndex].files.push(obj);
                        }
                    })


                    let acknowledgedItems = data.acknowledgedItems;
                    acknowledgedItems.forEach(item => {
                        let find = files.find(el => el.zipName === item.fileName);
                        let obj = { id: find.id, fileName: find.fileName, length: find.length, file: find.file }
                        invalidFiles[0].files.push(obj);
                    })

                    files.forEach(fileInfo => {
                        fileInfo.isChecked = true;
                    })

                    this.invalidFiles = [...this.invalidFiles, ...invalidFiles]
                    resolve();
                } else {
                    Message.error(response.errMsg);
                    this.terminateImport();
                }
            });
        })
    }

    sceneCheck(files, targetProjectId) {
        return new Promise((resolve) => {
            let paramsFiles = files.map(fileInfo => {
                let obj = {
                    sceneId: fileInfo.zipId,
                    sceneName: fileInfo.zipName,
                    resourceType: fileInfo.zipResType,
                }
                return obj;
            });
            let params = { targetProjectId, files: paramsFiles };
            sceneImportCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    let unEffectiveItems = data.unEffectiveItems;
                    let invalidFiles = [
                        {
                            isMsgBox: true,
                            isOverWrite: true,
                            errMsg: '场景在当前项目中已存在',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '文件资源类型错误',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '场景在其他项目中已存在',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '场景正在导入中',
                            files: []
                        }
                    ];
                    unEffectiveItems.forEach(item => {
                        let findIndex = invalidFiles.findIndex(el => item.errorMsg.includes(el.errMsg));
                        if (findIndex !== -1) {
                            let find = files.find(el => el.zipName === item.fileName);
                            let obj = { id: find.id, fileName: find.fileName, length: find.length, file: find.file }
                            invalidFiles[findIndex].files.push(obj);
                        }
                    })


                    let acknowledgedItems = data.acknowledgedItems;
                    acknowledgedItems.forEach(item => {
                        let find = files.find(el => el.zipName === item.fileName);
                        let obj = { id: find.id, fileName: find.fileName, length: find.length, file: find.file }
                        invalidFiles[0].files.push(obj);
                    })

                    files.forEach(fileInfo => {
                        fileInfo.isChecked = true;
                    })

                    this.invalidFiles = [...this.invalidFiles, ...invalidFiles]
                    resolve();
                } else {
                    Message.error(response.errMsg);
                    this.terminateImport();
                }
            });
        })
    }

    dataSetCheck(files, targetProjectId) {
        return new Promise(resolve => {
            let paramsFiles = files.map(fileInfo => {
                let obj = {
                    dataSetId: fileInfo.zipId,
                    dataSetName: fileInfo.zipName,
                    resourceType: fileInfo.zipResType,
                    scheme: fileInfo.zipScheme,
                    successVersion: fileInfo.zipVersion,
                }
                return obj;
            });
            let params = { targetProjectId, files: paramsFiles };
            dataSetImportCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    let unEffectiveItems = data.unEffectiveItems;
                    let invalidFiles = [
                        {
                            isMsgBox: true,
                            isOverWrite: true,
                            errMsg: '数据集在当前项目中已存在',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '文件资源类型错误',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '数据集在其他项目中已存在',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '数据集的版本号小于系统中的版本号',
                            files: []
                        },
                        {
                            isMsgBox: true,
                            isOverWrite: false,
                            errMsg: '数据集正在导入中',
                            files: []
                        }
                    ];
                    unEffectiveItems.forEach(item => {
                        let findIndex = invalidFiles.findIndex(el => item.errorMsg.includes(el.errMsg));
                        if (findIndex !== -1) {
                            let find = files.find(el => el.zipName === item.fileName);
                            let obj = { id: find.id, fileName: find.fileName, length: find.length, file: find.file }
                            invalidFiles[findIndex].files.push(obj);
                        }
                    })


                    let acknowledgedItems = data.acknowledgedItems;
                    acknowledgedItems.forEach(item => {
                        let find = files.find(el => el.zipName === item.fileName);
                        let obj = { id: find.id, fileName: find.fileName, length: find.length, file: find.file }
                        invalidFiles[0].files.push(obj);
                    })

                    files.forEach(fileInfo => {
                        fileInfo.isChecked = true;
                    })

                    this.invalidFiles = [...this.invalidFiles, ...invalidFiles];
                    resolve();
                } else {
                    Message.error(response.errMsg);
                    this.terminateImport();
                }
            });
        })
    }

    handleInvalidFile() {
        return new Promise(resolve => {
            let invalidFiles = this.invalidFiles.filter(el => el.files.length && el.isMsgBox);
            if (!invalidFiles.length) {
                resolve();
                return
            }

            let msgTips = [];
            let msgTail = '';
            let dataTypeNames = ['项目信息', '场景', '数据集'];
            let jumpFiles = invalidFiles.filter(el => !el.isOverWrite);
            jumpFiles.forEach(item => {
                msgTips.push(`${item.files.length}个${item.errMsg}`);
            })
            if (msgTips.length > 1) {
                msgTips = msgTips.map((item, index) => {
                    return `${index + 1}、${item}`
                });
            }

            if (!jumpFiles.length) {
                let msg = '';
                invalidFiles.forEach(item => {
                    msg = `${item.files.length}个${item.errMsg},是否覆盖`;
                })
                MessageBox.confirm(msg, '提示', {
                    iconClass: 'null',
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                }).then(() => {
                    invalidFiles.forEach(item => {
                        let findIndex = this.invalidFiles.find(el => el.errMsg === item.errMsg);
                        this.invalidFiles.splice(findIndex, 1);
                    })
                    resolve();
                }).catch(() => {
                    this.terminateImport();
                    resolve();
                })
                return;
            }

            if (this.isAllJump(invalidFiles)) {
                let message = this.createHtml(msgTips, msgTail);
                MessageBox.confirm(message, '提示', {
                    dangerouslyUseHTMLString: true,
                    confirmButtonText: '确定',
                    showClose: false,
                    showCancelButton: false,
                }).then(() => {
                    this.terminateImport();
                    resolve();
                })
                return;
            } else {
                let confirmFiles = invalidFiles.filter(el => el.isOverWrite);
                if (confirmFiles.length) {
                    if (this.importType === 0) {
                        msgTail = `是否跳过继续导入，并对已存在的${confirmFiles[0].files.length}个${dataTypeNames[this.importType]}进行覆盖`
                    } else {
                        msgTail = `是否跳过继续导入，并对当前项目中已存在的${confirmFiles[0].files.length}个${dataTypeNames[this.importType]}进行覆盖`;
                    }
                } else {
                    msgTail = '是否跳过继续导入';
                }
                let message = this.createHtml(msgTips, msgTail);
                MessageBox.confirm(message, '提示',
                    {
                        dangerouslyUseHTMLString: true,
                        iconClass: 'null',
                        confirmButtonText: '确定',
                        cancelButtonText: '取消',
                        type: 'warning',
                    }).then(() => {
                        let jumpFileArray = []
                        jumpFiles.forEach(item => {
                            jumpFileArray = [...jumpFileArray, ...item.files]
                        })
                        jumpFileArray.forEach(item => {
                            let findIndex = this.uploadFiles.findIndex(el => this.isSameFile(el, item));
                            if (findIndex !== -1) {
                                this.uploadFiles.splice(findIndex, 1);
                                this.uploader.removeFile(item.file, true);
                            }
                        })

                        invalidFiles.forEach(item => {
                            let findIndex = this.invalidFiles.find(el => el.errMsg === item.errMsg);
                            this.invalidFiles.splice(findIndex, 1)
                        })
                        resolve();
                    }).catch(() => {
                        this.terminateImport();
                        resolve();
                    })
            }
        })
    }

    handleWarningFile() {
        return new Promise((resolve) => {
            let warningFileArray = this.invalidFiles.filter(el => !el.isMsgBox);
            warningFileArray = warningFileArray.flatMap(item => item.files);
            warningFileArray.forEach(item => {
                Message.error(`${item.fileName}已在上传队列，请选择其他文件！！！`);
            });

            let warningFiles = this.invalidFiles.filter(el => !el.isMsgBox);
            warningFiles.forEach(item => {
                let findIndex = this.invalidFiles.find(el => el.errMsg === item.errMsg);
                this.invalidFiles.splice(findIndex, 1);
            })
            resolve();
        })
    }

    isAllJump(invalidFiles) {
        let count = 0;
        let isJumpFiles = invalidFiles.filter(el => !el.isOverWrite);
        isJumpFiles.forEach(item => {
            count += item.files.length;
        })
        if (count === this.fileTotal) {
            return true
        } else {
            return false
        }
    }

    createHtml(msgTips, msgTail) {
        let htmlString = '<div>';
        msgTips.forEach(item => {
            htmlString += `<p>${item}</p>`
        });
        htmlString += `<p>${msgTail}</p>`
        htmlString += '</div>'
        return htmlString;
    }

    terminateImport() {
        this.uploadFiles.forEach(item => {
            this.uploader.removeFile(item.file, true);
        })
        this.uploadFiles = [];
        this.fileTotal = 0;
        this.invalidFiles = [];
    }

    createFileUpLoad(fileInfo) {
        return new Promise((resolve) => {
            let count = fileInfo.length / (BLOB_SIZE);
            let params = {
                "fileName": fileInfo.fileName,
                "fileSize": fileInfo.length,
                "blobCount": Math.ceil(count),
                "blobSize": BLOB_SIZE
            }

            createImport(params).then((response) => {
                if (response.isSuccess) {
                    let result = response.data;
                    switch (fileInfo.importType) {
                        case 0: {
                            let formData = {
                                OwnerId: fileInfo.ownerId,
                                FileDataId: result.fileDataId,
                                Key: result.fileKey
                            };
                            this.uploader.options.formData = formData;
                            this.setAppendUrl("project");

                            resolve(result);
                            break;
                        }
                        case 1: {
                            let formData = {
                                ProjId: fileInfo.projId,
                                FileDataId: result.fileDataId,
                                Key: result.fileKey
                            };
                            this.uploader.options.formData = formData;
                            this.setAppendUrl("scene");

                            resolve(result);
                            break;
                        }
                        case 2: {
                            let formData = {
                                ProjId: fileInfo.projId,
                                TargetNodeId: fileInfo.targetNodeId,
                                FileDataId: result.fileDataId,
                                Key: result.fileKey
                            };
                            this.uploader.options.formData = formData;
                            this.setAppendUrl("dataset");

                            resolve(result);
                            break;
                        }
                    }
                } else {
                    let findIndex = this.uploadingFiles.findIndex(el => el.fileName === fileInfo.fileName && el.length === fileInfo.length)
                    if (findIndex !== -1) {
                        this.uploadingFiles.splice(findIndex, 1)
                        this.uploader.removeFile(fileInfo.file, true);
                    }

                    if (this.uploadFiles.length) {
                        let uploadingFile = this.uploadFiles.shift();
                        this.uploadingFiles.push(uploadingFile);
                        this.handleUploadSuccess(uploadingFile);
                    }
                }
            });
        });
    }

    handleUploadSuccess(uploadingFile) {
        if (uploadingFile.customStatus === 'waiting') {
            this.createFileUpLoad(uploadingFile).then((res) => {
                uploadingFile.customStatus = "created";
                uploadingFile.status = 0;
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.uploader.upload();
                }, 500);

            });
        }
        if (uploadingFile.customStatus === 'continue' || uploadingFile.customStatus === 'reContinue') {
            this.getFileInfo(uploadingFile).then(() => {
                uploadingFile.customStatus = "continue";
                uploadingFile.status = 1;

                this.continueFlag = true;
                this.setAppendUrl('reAppend');
                this.uploader.upload();
            });
        }
    }

    /**
    * 解析压缩包内容，获取压缩包的comment
    * @param {Object} fileInfo 
    * @returns 
    */
    analysisZipFile(fileInfo) {
        return new Promise(resolve => {
            let sourceFile = fileInfo.file.source.getSource();
            let zipFileReader = new zip.BlobReader(sourceFile);
            let zipReader = new zip.ZipReader(zipFileReader);
            zipReader.getEntries().then((response) => {
                let entry = response.find(el => el.filename.includes('readme.json'));
                if (entry) {
                    const helloWorldWriter = new zip.TextWriter();
                    entry.getData(helloWorldWriter).then(res => {
                        let comment = JSON.parse(res);
                        fileInfo.zipExportType = Number(comment.ExportType);
                        fileInfo.zipProjId = comment.ProjId;
                        fileInfo.zipId = comment.Id;
                        fileInfo.zipName = comment.Name;
                        fileInfo.zipResType = comment.ResType;
                        fileInfo.zipScheme = comment.Scheme;
                        fileInfo.zipFormatType = comment.FormatType;
                        fileInfo.zipVersion = comment.Version;
                        resolve(fileInfo);
                    })
                } else {
                    fileInfo.isInvalid = true;
                    resolve(fileInfo)
                }
            })
        })
    }

    /**
     * 检验文件类型是否与导入类型一致
     * @param {Object} fileInfo
     */
    isInvalidImportTypeFile(fileInfo) {
        return fileInfo.zipExportType !== this.importType || fileInfo.isInvalid;
    }

    /**
     * 校验是否为同一文件
     * @param {Object} fileInfo1 
     * @param {Object} fileInfo2
     * @returns 
     */
    isSameFile(fileInfo1, fileInfo2) {
        return fileInfo1.fileName === fileInfo2.fileName && fileInfo1.length === fileInfo2.length;
    }

    /**
     * 获取已上传文件信息，用于分片上传判断上传到哪一片
     * @param {Object} fileInfo 
     */
    getFileInfo(fileInfo) {
        return new Promise(resolve => {
            getFileInfoService(fileInfo.fileDataId).then((response) => {
                if (response.isSuccess) {
                    let data = response.data;
                    let bussinessInfo = data.fileBusinessInfo
                        ? data.fileBusinessInfo
                        : {};
                    if (this.importType === 0) {
                        this.uploader.options.formData['OwnerId'] = bussinessInfo.ProjectOwnerId;
                    } else {
                        this.uploader.options.formData['ProjId'] = bussinessInfo.projId;
                    }

                    this.uploader.options.formData["FileDataId"] = data.id;
                    this.uploader.options.formData["Key"] = data.key;

                    fileInfo["blobIndex"] = data.blobIndex;
                    resolve();
                }
            })
        })
    }

    getFileStatus() {
        let status = null;
        switch (this.status) {
            case 'waiting':
                status = -1;
                break;
            case 'continue':
                status = -2;
                break;
            case 'reContinue':
                status = -2;
                break;

        }
        return status;
    }

    setImportType(importType) {
        this.importType = importType;
    }

    setAppendUrl(importType) {
        if (importType === 'reAppend') {
            this.uploader.options.server = `${window.serverUrl}/${logServeCof.logServeCof()}/project/dataSetMigration/v3/import/reAppend`;
        } else {
            this.uploader.options.server = `${window.serverUrl}/${logServeCof.logServeCof()}/project/dataSetMigration/v3/import/${importType}/append`;
        }
    }

    setStatus(status) {
        this.status = status;
    }

    setCurrFileInfo(fileInfo) {
        this.currFileInfo = fileInfo;
    }
}


export default ImportUploader;